import { Controller } from '@hotwired/stimulus'

/*
 * authorize-net-checkout
 *
 * Owns the Authorize.net AcceptUI lightbox lifecycle for in-place checkout.
 * The `.AcceptUI` button (a `button` target) opens Authorize.net's hosted card
 * lightbox; on tokenization, Accept.js calls the global responseHandler this
 * controller binds. On success the opaque nonce is POSTed to the gateway charge
 * endpoint, which answers JSON `{ redirect }` that we navigate to (no client-side
 * eval). Recovery UI (fallback button, error message, watchdog timer) covers a
 * lightbox that never opens, is dismissed, errors, or a stalled POST.
 *
 * Modes:
 *   autoOpen=true  -> open the lightbox on connect (the legacy /new page).
 *   autoOpen=false -> caller opens it via openLightbox() after an XHR pre-step
 *                     that transitions the order to payment_initiated. The membership
 *                     form's submit is wired to startMembership(), which runs that
 *                     pre-step and re-renders the form in place on validation failure.
 */
export default class extends Controller {
  static values = { autoOpen: Boolean, chargeUrl: String, entryUrl: String }
  static targets = ['button', 'spinner', 'fallback', 'error']

  connect() {
    this.fallbackTimer = null
    // Accept.js looks up the handler named in data-responsehandler on window.
    this.boundHandler = this.responseHandler.bind(this)
    window.responseHandler = this.boundHandler
    if (this.autoOpenValue) {
      // Arm the stuck-state watchdog now (connect fires ~DOMContentLoaded), NOT at
      // window.load: load waits for every asset, so a single hanging image on a spotty
      // mobile connection would delay the safety net for minutes — the exact condition
      // #13020 recovers from. openLightbox() re-checks before popping a lightbox.
      this.scheduleFallbackCheck()
      // Accept.js binds the .AcceptUI click at readyState 'complete'; click once it has.
      if (document.readyState === 'complete') {
        this.openLightbox()
      } else {
        window.addEventListener('load', () => this.openLightbox(), {
          once: true,
        })
      }
    }
  }

  disconnect() {
    clearTimeout(this.fallbackTimer)
    if (window.responseHandler === this.boundHandler) {
      window.responseHandler = undefined
    }
  }

  // Membership entry: submit the answer form via XHR instead of a full navigation.
  // A validation failure comes back as 422 HTML that replaces the swappable form
  // body in place (typed answers preserved, #1407); success returns a charge_url to
  // open the lightbox over the form, or a redirect for free/zero-total orders.
  async startMembership(event) {
    event.preventDefault()
    const form = event.currentTarget
    const trigger = form.querySelector(
      'button[type="submit"], input[type="submit"]',
    )
    if (trigger) trigger.disabled = true

    // Bound the entry POST so a stalled request (a TCP hang fires neither resolve nor
    // reject) surfaces the retry UI instead of a permanently disabled button.
    const abort = new AbortController()
    const timeout = setTimeout(() => abort.abort(), 30000)

    try {
      const resp = await fetch(form.action, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        // FormData carries Rails' authenticity_token from the form.
        body: new FormData(form),
        // A session-expiry redirect (Devise) must not be silently followed.
        redirect: 'manual',
        signal: abort.signal,
      })
      // Validation failure: swap the form body in place so typed answers survive.
      if (resp.status === 422) {
        const body = document.getElementById('membership-form-body')
        if (body) body.innerHTML = await resp.text()
        if (trigger) trigger.disabled = false
        // Re-run the page's re-init hooks on the swapped-in DOM (both defined in
        // views/dynamic_forms/_show_js.html.erb): bindDynamicForms rewires the
        // conditional-question show/hide logic, reinitProfileForms rebinds the
        // country->state cascade (CampusESP.MyProfile) so the State dropdown keeps
        // populating. Optional-chained: an unexpected absence is a cosmetic miss, not
        // a payment failure — a bare call would throw into the catch below and surface
        // the payment-error fallback UI.
        window.bindDynamicForms?.()
        window.reinitProfileForms?.()
        return
      }
      if (resp.status === 401 || resp.type === 'opaqueredirect') {
        throw new Error('session')
      }
      if (!resp.ok) throw new Error(String(resp.status))
      const data = await resp.json()
      // Free/zero-total (or a non-lightbox gateway): just navigate.
      if (data.redirect) {
        window.location = data.redirect
        return
      }
      // The order was created server-side; open the lightbox over the form.
      if (data.charge_url) this.chargeUrlValue = data.charge_url
      this.openLightbox()
    } catch (err) {
      if (trigger) trigger.disabled = false
      const message =
        err.message === 'session'
          ? 'Your session has expired. Please reload the page and sign in again.'
          : "We couldn't start checkout. Please try again, or reload the page."
      this.showFallback(message)
    } finally {
      clearTimeout(timeout)
    }
  }

  // Event entry: POST the review page's submit_order via XHR to move the order to
  // payment_initiated, then open the lightbox in place. There's no form to submit, so
  // the CSRF token rides as a header. A ready handoff opens the lightbox; a redirect
  // (free/zero-total) navigates.
  async startInPlace(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    trigger.disabled = true
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    // Bound the entry POST so a stalled request surfaces the retry UI instead of a
    // permanently disabled button (mirrors submitCharge's guard).
    const abort = new AbortController()
    const timeout = setTimeout(() => abort.abort(), 30000)

    try {
      const resp = await fetch(this.entryUrlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(token ? { 'X-CSRF-Token': token } : {}),
        },
        // A session-expiry redirect (Devise) must not be silently followed.
        redirect: 'manual',
        signal: abort.signal,
      })
      if (resp.status === 401 || resp.type === 'opaqueredirect') {
        throw new Error('session')
      }
      if (!resp.ok) throw new Error(String(resp.status))
      const data = await resp.json()
      // Free/zero-total (or a non-lightbox gateway): just navigate.
      if (data.redirect) {
        window.location = data.redirect
        return
      }
      if (data.charge_url) this.chargeUrlValue = data.charge_url
      this.openLightbox()
    } catch (err) {
      trigger.disabled = false
      const message =
        err.message === 'session'
          ? 'Your session has expired. Please reload the page and sign in again.'
          : "We couldn't start checkout. Please try again, or reload the page."
      this.showFallback(message)
    } finally {
      clearTimeout(timeout)
    }
  }

  // Open the lightbox (unless the manual fallback is already showing) and arm the
  // watchdog so a silent no-open — Accept.js not yet bound, or a browser that blocks
  // the programmatic click — still surfaces the fallback, not a dead spinner.
  openLightbox() {
    if (this.fallbackVisible()) return
    this.buttonTarget.click()
    this.scheduleFallbackCheck()
  }

  // Re-check on each tick so we never pop the fallback behind an OPEN lightbox.
  scheduleFallbackCheck() {
    clearTimeout(this.fallbackTimer)
    this.fallbackTimer = setTimeout(() => {
      const container = document.getElementById('AcceptUIContainer')
      if (container && container.classList.contains('show')) {
        this.scheduleFallbackCheck()
      } else {
        this.showFallback()
      }
    }, 7000)
  }

  showFallback(message) {
    clearTimeout(this.fallbackTimer)
    if (this.hasSpinnerTarget) this.spinnerTarget.style.display = 'none'
    if (message && this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.style.display = ''
    }
    if (this.hasFallbackTarget) this.fallbackTarget.style.display = ''
  }

  fallbackVisible() {
    return (
      this.hasFallbackTarget &&
      this.fallbackTarget.style.display !== 'none' &&
      this.fallbackTarget.offsetParent !== null
    )
  }

  // Bound to window for Accept.js. Receives the tokenization response.
  responseHandler(response) {
    clearTimeout(this.fallbackTimer)
    if (response.messages.resultCode === 'Error') {
      response.messages.message.forEach((m) =>
        console.log(`${m.code}: ${m.text}`),
      )
      this.showFallback(
        "We couldn't read your card details. Please check them and tap Pay Now to try again.",
      )
      return
    }
    this.submitCharge(response.opaqueData)
  }

  async submitCharge(opaqueData) {
    // Fail closed: a blank charge URL means the entry step never handed one back
    // (e.g. a 500 before the ready handoff). Don't POST the nonce to the current page.
    if (!this.chargeUrlValue) {
      this.showFallback(
        'Something went wrong starting checkout. Please reload the page and try again.',
      )
      return
    }
    this.buttonTarget.disabled = true
    if (this.hasSpinnerTarget) this.spinnerTarget.style.display = ''
    if (this.hasErrorTarget) this.errorTarget.style.display = 'none'
    if (this.hasFallbackTarget) this.fallbackTarget.style.display = 'none'

    const params = new URLSearchParams()
    params.append('opaque_data[dataDescriptor]', opaqueData.dataDescriptor)
    params.append('opaque_data[dataValue]', opaqueData.dataValue)

    const abort = new AbortController()
    // Bound the submit so a stalled POST (a TCP hang fires neither resolve nor reject)
    // surfaces the recovery UI instead of an indefinite spinner. A false timeout on a
    // charge that actually went through is safe — the server's completed-payment
    // idempotency guard redirects a retry to the receipt rather than charging twice.
    const timeout = setTimeout(() => abort.abort(), 30000)

    try {
      const resp = await fetch(this.chargeUrlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: params,
        // A session-expiry redirect (Devise) must not be silently followed.
        redirect: 'manual',
        signal: abort.signal,
      })
      // Devise answers XHR with 401; a manual-mode 302 surfaces as opaqueredirect.
      if (resp.status === 401 || resp.type === 'opaqueredirect') {
        throw new Error('session')
      }
      if (!resp.ok) throw new Error(String(resp.status))
      const data = await resp.json()
      // Fail into the recovery UI rather than navigating to ".../undefined" if the
      // contract is ever violated (the gateway #create always returns { redirect }).
      if (!data?.redirect) throw new Error('no-redirect')
      window.location = data.redirect
    } catch (err) {
      this.buttonTarget.disabled = false
      const message =
        err.message === 'session'
          ? 'Your session has expired. Please reload the page and sign in again.'
          : 'Something went wrong submitting your payment. Please tap Pay Now to try again — if it keeps happening, reload the page.'
      this.showFallback(message)
      // Restart the watchdog so a re-opened lightbox that also hangs is still caught.
      this.scheduleFallbackCheck()
    } finally {
      clearTimeout(timeout)
    }
  }
}
